import './SplitHero.css';
import tapngoLogo from '../../assets/tapngo.svg';
import { AdinkraBackground, MonumentSilhouette } from '../SignupArt/SignupArt';

/**
 * Page hero laid out like the signup page.
 *
 * Desktop (lg+): image panel on the left (2/5 width, full hero height),
 * text on the right, vertically centred.
 * Mobile/tablet: text first, then a short rounded image below it.
 *
 * Props:
 *  - title:     heading content (string or node)
 *  - subtitle:  optional paragraph under the heading (string or node)
 *  - image:     image src for the media panel
 *  - imageAlt:  alt text for the image ('' for decorative)
 *  - imageFit:  'cover' (photos, default) or 'contain' (illustrations and
 *               device mockups, shown on a light surface)
 *  - media:     optional node rendered in the media panel instead of <img>
 *  - brand:     true renders the signup page's green brand panel (gradient,
 *               monument and logo) instead of an image
 *  - brandTitle, brandText: optional heading and line shown on the brand panel
 *  - pattern:   true puts the signup page's Adinkra pattern behind the hero
 *  - id:        optional id for the <section>
 *  - children:  extra content below the subtitle (store buttons, CTAs)
 */
const SplitHero = ({
  title,
  subtitle,
  image,
  imageAlt = '',
  imageFit = 'cover',
  media,
  brand = false,
  brandTitle,
  brandText,
  pattern = false,
  id,
  children,
}) => {
  const contain = imageFit === 'contain' || Boolean(media);

  return (
    <section
      id={id}
      className={`split-hero relative flex flex-col lg:flex-row font-figtree ${
        pattern ? 'bg-secondary-surface' : 'bg-white'
      }`}
    >
      {pattern && (
        <AdinkraBackground className="absolute inset-0 overflow-hidden" />
      )}

      {/* Text — first on mobile, right column on desktop */}
      <div className="relative order-1 lg:order-2 flex-1 flex flex-col justify-center px-4 pt-10 pb-6 sm:px-6 md:px-10 lg:px-12 xl:px-16 lg:py-16">
        <div className="max-w-xl">
          <h1 className="text-default text-3xl md:text-4xl xl:text-5xl font-bold leading-tight mb-4">
            {title}
          </h1>
          {subtitle && (
            <p className="text-subtext text-base md:text-lg">{subtitle}</p>
          )}
          {children}
        </div>
      </div>

      {/* Media — after the text on mobile, left panel on desktop */}
      <div className="relative order-2 lg:order-1 px-4 pb-10 sm:px-6 md:px-10 lg:p-0 lg:w-2/5 lg:flex-shrink-0 lg:flex lg:flex-col">
        <div
          className={`relative h-56 sm:h-64 lg:h-auto lg:flex-1 overflow-hidden rounded-2xl lg:rounded-none ${
            contain ? 'bg-secondary-surface' : 'bg-gray-100'
          }`}
          style={
            brand
              ? {
                  background:
                    'linear-gradient(to top, #0A3F28 0%, #12724A 55%, #1C9A62 100%)',
                }
              : undefined
          }
        >
          {brand ? (
            <>
              <MonumentSilhouette className="absolute inset-x-0 bottom-0 h-4/5" />
              <div className="relative z-10 p-6 lg:p-12 xl:p-16 text-white">
                <img
                  src={tapngoLogo}
                  alt=""
                  className="h-10 w-10 lg:h-12 lg:w-12 rounded-xl shadow-lg mb-4 lg:mb-10"
                />
                {brandTitle && (
                  <p className="text-xl lg:text-3xl xl:text-4xl font-bold leading-tight max-w-xs lg:max-w-sm text-white">
                    {brandTitle}
                  </p>
                )}
                {brandText && (
                  <p className="hidden lg:block mt-4 text-base text-white opacity-80 max-w-sm">
                    {brandText}
                  </p>
                )}
              </div>
            </>
          ) : media ? (
            <div className="absolute inset-0 flex items-center justify-center p-4 lg:p-12">
              {media}
            </div>
          ) : (
            <img
              src={image}
              alt={imageAlt}
              className={`absolute inset-0 w-full h-full ${
                contain ? 'object-contain p-4 lg:p-12' : 'object-cover'
              }`}
            />
          )}
        </div>
      </div>
    </section>
  );
};

export default SplitHero;
